import React from "react";
import {
  fetchModbusStatus,
  setSitePollingInterval,
  fetchSiteLatestValues,
} from "../api/modbus";
import { updateSiteAcquisitionMode } from "../api/sites";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString();
};

const formatRelative = (value) => {
  if (!value) return "";
  const now = Date.now();
  const target = new Date(value).getTime();
  const diffSec = Math.max(0, Math.floor((now - target) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}d ago`;
};

const Banner = ({ state, onClose }) => {
  if (!state?.text) return null;
  const styles =
    state.type === "error"
      ? "bg-rose-50 text-rose-700 border border-rose-200"
      : "bg-emerald-50 text-emerald-700 border border-emerald-200";
  return (
    <div className={`mb-4 flex items-start justify-between rounded-xl px-4 py-3 text-sm ${styles}`}>
      <span>{state.text}</span>
      <button
        className="text-xs font-semibold uppercase tracking-wide text-slate-500"
        onClick={onClose}
        type="button"
      >
        Dismiss
      </button>
    </div>
  );
};

const acquisitionModes = [
  { value: "gateway_api", label: "API" },
  { value: "off", label: "OFF" },
  { value: "modbus", label: "MODBUS" },
];

const ModeControl = ({ value, disabled, onChange }) => (
  <div className="inline-grid grid-cols-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-1">
    {acquisitionModes.map((mode) => {
      const active = value === mode.value;
      return (
        <button
          key={mode.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(mode.value)}
          className={`min-w-20 rounded-lg px-3 py-2 text-xs font-semibold transition ${
            active ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-white"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          {mode.label}
        </button>
      );
    })}
  </div>
);

const statusLabel = (site) => {
  if (site.acquisition_mode === "gateway_api") return "Gateway API";
  if (site.acquisition_mode === "off") return "Off";
  return site.worker_running ? "Running" : "Stopped";
};

const statusStyles = (site) => {
  if (site.acquisition_mode === "gateway_api") return "bg-blue-100 text-blue-700";
  if (site.acquisition_mode === "off") return "bg-slate-100 text-slate-600";
  return site.worker_running ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
};

export default function ModbusStatus() {
  const [sites, setSites] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [intervalInputs, setIntervalInputs] = React.useState({});
  const [banner, setBanner] = React.useState(null);
  const [actionSite, setActionSite] = React.useState(null);
  const [errorModal, setErrorModal] = React.useState({ open: false, site: null, errors: [] });
  const [dataModal, setDataModal] = React.useState({ open: false, site: null, rows: [], loading: false, error: "" });
  const [latestValueFilter, setLatestValueFilter] = React.useState("");

  const loadStatus = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchModbusStatus();
      setSites(data);
      const inputs = {};
      data.forEach((site) => {
        inputs[site.site_id] = site.default_poll_interval || 60;
      });
      setIntervalInputs(inputs);
    } catch (error) {
      setBanner({ type: "error", text: "Failed to load Modbus status. Please try again." });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleIntervalChange = (siteId, value) => {
    setIntervalInputs((prev) => ({
      ...prev,
      [siteId]: value,
    }));
  };

  const applyInterval = async (siteId) => {
    const value = Number(intervalInputs[siteId]);
    if (!Number.isFinite(value) || value < 1) {
      setBanner({ type: "error", text: "Polling interval must be a positive number." });
      return;
    }
    setActionSite(siteId);
    try {
      await setSitePollingInterval(siteId, Math.round(value));
      setBanner({ type: "success", text: "Polling interval updated for selected site." });
      await loadStatus();
    } catch (error) {
      setBanner({ type: "error", text: "Unable to update polling interval." });
    } finally {
      setActionSite(null);
    }
  };

  const changeAcquisitionMode = async (site, mode) => {
    if (site.acquisition_mode === mode) return;
    setActionSite(site.site_id);
    try {
      await updateSiteAcquisitionMode(site.site_id, mode);
      setBanner({ type: "success", text: `Acquisition mode updated for ${site.site_name}.` });
      await loadStatus();
    } catch (error) {
      setBanner({ type: "error", text: `Unable to update acquisition mode for ${site.site_name}.` });
    } finally {
      setActionSite(null);
    }
  };

  const showErrors = (site) => {
    setErrorModal({
      open: true,
      site,
      errors: site.errors || [],
    });
  };

  const openLatestValues = async (site) => {
    setLatestValueFilter("");
    setDataModal({ open: true, site, rows: [], loading: true, error: "" });
    try {
      const rows = await fetchSiteLatestValues(site.site_id);
      setDataModal({ open: true, site, rows, loading: false, error: "" });
    } catch (error) {
      setDataModal({
        open: true,
        site,
        rows: [],
        loading: false,
        error: "Failed to load latest values.",
      });
    }
  };

  const closeDataModal = () => {
    setLatestValueFilter("");
    setDataModal({ open: false, site: null, rows: [], loading: false, error: "" });
  };

  const filteredLatestRows = dataModal.rows.filter((row) => {
    const query = latestValueFilter.trim().toLowerCase();
    if (!query) return true;
    return [
      row.signal_name,
      row.signal_source_name,
      row.signal_type,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query));
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Modbus status</h1>
        <p className="mt-2 text-sm text-slate-600">
          Live connectivity, acquisition mode and direct Modbus polling controls for configured sites.
        </p>
      </div>

      <Banner state={banner} onClose={() => setBanner(null)} />

      {loading ? (
        <div className="rounded-2xl bg-white p-10 text-center text-slate-500 shadow-sm">Loading site data…</div>
      ) : sites.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center text-slate-500 shadow-sm">
          No sites available yet. Configure a site to start monitoring Modbus polling.
        </div>
      ) : (
        <div className="space-y-4">
          {sites.map((site) => {
            const pollingValue = intervalInputs[site.site_id] ?? 60;
            const hasErrors = site.errors?.length > 0;
            const lastPollTs = site.last_poll_timestamp;
            const lastValueCount = site.last_poll_value_count ?? 0;
            const recentValueCount = site.values_received_last_minute ?? 0;
            const modbusMode = site.acquisition_mode === "modbus";
            return (
              <div
                key={site.site_id}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-slate-300"
              >
                <div className="grid gap-6 lg:grid-cols-[1.35fr,1fr,1fr,1fr]">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Site name</div>
                    <div className="text-xl font-semibold text-slate-900">{site.site_name}</div>
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-wide text-slate-500"># mapped signals</div>
                      <div className="text-4xl font-semibold text-slate-900">{site.mapped_signals}</div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2 text-sm text-slate-500">
                      <span>Last successful write:</span>
                      <span className="font-medium text-slate-900">{formatTimestamp(lastPollTs)}</span>
                      <span className="text-slate-400">{formatRelative(lastPollTs)}</span>
                    </div>
                    <div className="text-sm text-slate-500">
                      Latest successful write stored{" "}
                      <span className="font-semibold text-slate-900">{lastValueCount}</span> values / received{" "}
                      <span className="font-semibold text-slate-900">{recentValueCount}</span> values in the last minute
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Acquisition mode</div>
                    <ModeControl
                      value={site.acquisition_mode || "modbus"}
                      disabled={actionSite === site.site_id}
                      onChange={(mode) => changeAcquisitionMode(site, mode)}
                    />
                    <div className="text-sm text-slate-500">
                      {site.acquisition_mode === "gateway_api"
                        ? "Gateway uploads are authoritative for this site."
                        : site.acquisition_mode === "off"
                          ? "Direct polling and gateway telemetry are disabled."
                          : "PVapp direct Modbus polling is authoritative."}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Current state</div>
                    <div
                      className={`inline-flex rounded-full px-4 py-1 text-sm font-semibold ${statusStyles(site)}`}
                    >
                      {statusLabel(site)}
                    </div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Errors</div>
                    {hasErrors ? (
                      <div className="flex flex-col gap-2">
                        <div className="text-sm font-medium text-rose-600">{site.errors.length} issue(s) detected</div>
                        <button
                          type="button"
                          onClick={() => showErrors(site)}
                          className="w-fit rounded-lg border border-rose-200 px-3 py-1 text-sm font-medium text-rose-600 hover:bg-rose-50"
                        >
                          View details
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-emerald-600">No active errors</div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Polling interval (seconds)
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min={1}
                        value={pollingValue}
                        onChange={(event) => handleIntervalChange(site.site_id, event.target.value)}
                        disabled={!modbusMode}
                        className="w-24 rounded-xl border border-slate-300 px-3 py-2 text-center text-lg font-semibold text-slate-900 focus:border-blue-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => applyInterval(site.site_id)}
                        disabled={!modbusMode || actionSite === site.site_id}
                        className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        SET
                      </button>
                    </div>
                    <button
                      type="button"
                      className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                      onClick={() => openLatestValues(site)}
                    >
                      Show latest values
                    </button>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Errors modal */}
      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal((prev) => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modbus errors — {errorModal.site?.site_name}</DialogTitle>
            <p className="text-sm text-slate-500">
              These alerts are derived from worker status, helper-table snapshots and recent poll timestamps.
            </p>
          </DialogHeader>
          {errorModal.errors.length === 0 ? (
            <div className="rounded-xl bg-emerald-50 px-4 py-3 text-emerald-700">No errors to show.</div>
          ) : (
            <ul className="list-disc space-y-2 pl-5 text-sm text-slate-700">
              {errorModal.errors.map((err, idx) => (
                <li key={`${err}-${idx}`}>{err}</li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Latest values modal */}
      <Dialog open={dataModal.open} onOpenChange={(open) => (open ? null : closeDataModal())}>
        <DialogContent className="max-h-[85vh] w-[min(640px,90vw)] max-w-none overflow-hidden">
          <DialogHeader>
            <DialogTitle>Latest Modbus values — {dataModal.site?.site_name}</DialogTitle>
            <p className="text-sm text-slate-500">
              Latest known value per active signal for this site. Use the filter to find tags such as AIRTEMP, IRRAD
              or STATUS1.
            </p>
          </DialogHeader>
          {dataModal.loading ? (
            <div className="rounded-xl bg-slate-50 px-4 py-8 text-center text-slate-500">Loading…</div>
          ) : dataModal.error ? (
            <div className="rounded-xl bg-rose-50 px-4 py-3 text-rose-700">{dataModal.error}</div>
          ) : dataModal.rows.length === 0 ? (
            <div className="rounded-xl bg-slate-50 px-4 py-8 text-center text-slate-500">No latest values yet.</div>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                value={latestValueFilter}
                onChange={(event) => setLatestValueFilter(event.target.value)}
                placeholder="Filter by signal name, tag or type"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              />
              <div className="max-h-[65vh] overflow-y-auto rounded-xl border border-slate-100">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Timestamp</th>
                      <th className="px-3 py-2">Signal tag</th>
                      <th className="px-3 py-2">Signal type</th>
                      <th className="px-3 py-2 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLatestRows.map((row, index) => (
                      <tr key={`${row.timestamp}-${row.signal_source_name}-${index}`}>
                        <td className="px-3 py-2 text-slate-700">{formatTimestamp(row.timestamp)}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{row.signal_source_name || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{row.signal_type}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                          {row.value == null
                            ? "—"
                            : Number(row.value).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredLatestRows.length === 0 ? (
                <div className="rounded-xl bg-slate-50 px-4 py-3 text-center text-sm text-slate-500">
                  No values match the current filter.
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
